import { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { RecordContextProvider, useRecordContext, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { EmptyField } from '@/components/accessKeys/fields/EmptyField.jsx';
import CustomChipField from '@/components/global/fields/CustomChipField.jsx';
import { useDrawer } from '@/composables/useDrawer';
import { Palette } from '@/themes/themeOverrides/Palette';
import ArrowDownCollapseIcon from '@/assets/icons/arrow-down-collapse-icon.svg?react';

const CHIP_GAP = 4;
const ROW_MAX_WIDTH = 350;

const expandChipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    margin: 0,
    padding: '4px 8px 4px 12px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: Palette.violet.violet50,
    color: Palette.black,
    lineHeight: '16px',
    cursor: 'pointer',
    '& [data-expand-label]:not(:empty)': {
        marginRight: '2px',
    },
    '& .MuiTypography-root': {
        fontSize: '11px !important',
        lineHeight: '16px !important',
    },
    '& svg': {
        display: 'block',
        width: '20px',
        height: '20px',
        margin: '-2px 0px',
        transform: 'rotate(180deg)',
    },
    '&:hover': {
        backgroundColor: Palette.violet.violet100,
    },
};

const ExpandBrandsChip = ({ hiddenCount = 0, onClick, measure = false }) => {
    const translate = useTranslate();
    const label = hiddenCount > 0 ? `+${hiddenCount}` : '';

    return (
        <Box
            component='button'
            type='button'
            data-expand-chip
            data-measure={measure ? '' : undefined}
            aria-hidden={measure || undefined}
            aria-label={measure ? undefined : `${translate('ra.action.expand')} ${label}`}
            tabIndex={measure ? -1 : 0}
            onClick={event => {
                event.stopPropagation();
                onClick?.();
            }}
            sx={{
                ...expandChipSx,
                ...(measure ? { position: 'absolute', visibility: 'hidden', pointerEvents: 'none' } : null),
            }}
        >
            <Typography component='span' data-expand-label variant='body2' sx={{ fontSize: '11px', lineHeight: '16px' }}>
                {label}
            </Typography>
            <ArrowDownCollapseIcon />
        </Box>
    );
};

ExpandBrandsChip.propTypes = {
    hiddenCount: PropTypes.number,
    onClick: PropTypes.func,
    measure: PropTypes.bool,
};

const BrandChip = () => {
    const brand = useRecordContext();
    const { openDrawer } = useDrawer();

    if (!brand?.name) {
        return null;
    }

    const collectionPointCount = brand.collectionPointCount ?? 0;

    return (
        <RecordContextProvider value={{ ...brand, name: `${brand.name} (${collectionPointCount})` }}>
            <CustomChipField small shades color={Palette.violet.violet50} onClick={brand?.id ? () => openDrawer(brand.id, 'brand-show') : undefined} />
        </RecordContextProvider>
    );
};

BrandChip.propTypes = {};

const countFittingBrands = row => {
    const chipNodes = [...row.querySelectorAll('[data-brand-chip]')];
    const expandNode = row.querySelector('[data-expand-chip]');
    const maxWidth = row.clientWidth;
    const expandWidth = expandNode?.offsetWidth ?? 0;
    let used = 0;
    let count = 0;

    const allChipsWidth = chipNodes.reduce((width, chip) => width + chip.offsetWidth, 0) + Math.max(chipNodes.length - 1, 0) * CHIP_GAP;
    if (allChipsWidth <= maxWidth) {
        return chipNodes.length;
    }

    for (let index = 0; index < chipNodes.length; index += 1) {
        const chipWidth = chipNodes[index].offsetWidth;
        const isLast = index === chipNodes.length - 1;
        const widthWithGap = chipWidth + (count > 0 ? CHIP_GAP : 0);
        const reserved = isLast ? 0 : expandWidth + CHIP_GAP;

        if (used + widthWithGap + reserved > maxWidth) {
            break;
        }

        used += widthWithGap;
        count += 1;
    }

    return count;
};

export const RetailChainBrandsField = () => {
    const record = useRecordContext();
    const brands = Array.isArray(record?.brands) ? record.brands : [];
    const brandsKey = JSON.stringify(brands.map(brand => [brand?.id, brand?.name, brand?.collectionPointCount]));
    const rowRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(brands.length);

    useLayoutEffect(() => {
        setExpanded(false);
    }, [brandsKey]);

    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row || expanded) {
            return undefined;
        }

        const updateVisibleCount = () => {
            const labelNode = row.querySelector('[data-measure] [data-expand-label]');
            let count = brands.length;

            for (let step = 0; step <= brands.length; step += 1) {
                const hidden = brands.length - count;
                if (labelNode) {
                    labelNode.textContent = hidden > 0 ? `+${hidden}` : '';
                }
                const nextCount = countFittingBrands(row);
                if (nextCount === count) {
                    break;
                }
                count = nextCount;
            }

            setVisibleCount(current => (current === count ? current : count));
        };

        updateVisibleCount();
        const observer = new ResizeObserver(updateVisibleCount);
        observer.observe(row);

        return () => observer.disconnect();
    }, [brands.length, brandsKey, expanded]);

    if (brands.length === 0) {
        return <EmptyField />;
    }

    const hiddenCount = Math.max(brands.length - visibleCount, 0);
    const hasHiddenBrands = !expanded && hiddenCount > 0;

    return (
        <Box
            ref={rowRef}
            sx={{
                display: 'flex',
                flexWrap: expanded ? 'wrap' : 'nowrap',
                gap: `${CHIP_GAP}px`,
                width: `${ROW_MAX_WIDTH}px`,
                maxWidth: '100%',
                minWidth: 0,
                position: 'relative',
                alignItems: 'center',
                overflow: expanded ? 'visible' : 'hidden',
            }}
        >
            {brands.map((brand, index) => {
                const hidden = !expanded && index >= visibleCount;

                return (
                    <Box
                        key={brand?.id ?? `${brand?.name}-${index}`}
                        data-brand-chip
                        sx={hidden ? { position: 'absolute', visibility: 'hidden', pointerEvents: 'none' } : { flexShrink: 0 }}
                    >
                        <RecordContextProvider value={brand}>
                            <BrandChip />
                        </RecordContextProvider>
                    </Box>
                );
            })}
            {!expanded ? <ExpandBrandsChip measure hiddenCount={hiddenCount} /> : null}
            {hasHiddenBrands ? <ExpandBrandsChip hiddenCount={hiddenCount} onClick={() => setExpanded(true)} /> : null}
        </Box>
    );
};

RetailChainBrandsField.propTypes = {};
